import type { CollectiblesConfig } from '../config/collectibles.config';

/**
 * Turbo of the beer crate (user 2026-10-03): for a few seconds the runner is invulnerable, blinks and runs faster.
 * Pure logic – the RunDirector counts it down with the simulation (quiz, drink break and pause hold it), the Game
 * scales the run speed with `speedFactor` and the PlayerView blinks while it is active.
 *
 * The speed does not jump: it rises to the full factor within `rampSeconds` and falls back to 1 within the last
 * `rampSeconds` of the turbo. A second crate during the turbo keeps the full speed (only the duration restarts).
 */
export class TurboBoost {
  private seconds = 0;
  /** Ramp-up progress 0 … 1 since the turbo started. */
  private rise = 0;

  constructor(private readonly config: CollectiblesConfig['turbo']) {}

  get active(): boolean {
    return this.seconds > 0;
  }

  /** Remaining share of the turbo, 1 … 0 (HUD bar). */
  get fraction(): number {
    return this.config.seconds > 0 ? this.seconds / this.config.seconds : 0;
  }

  /** Multiplier of the run speed – 1 without turbo, the full factor between the two ramps. */
  get speedFactor(): number {
    if (!this.active) return 1;
    const ramp = this.config.rampSeconds ?? 0;
    const envelope = ramp > 0 ? Math.min(this.rise, this.seconds / ramp, 1) : 1;
    return 1 + (this.config.speedFactor - 1) * envelope;
  }

  /** Full length of one turbo (seconds). */
  get durationSeconds(): number {
    return this.config.seconds;
  }

  /** Invulnerability after the turbo ends (seconds). */
  get graceSeconds(): number {
    return this.config.graceSeconds;
  }

  /** Crate picked up: the turbo (re)starts with its full duration – from the speed it has now (no jump in the ramp-out). */
  activate(): void {
    const ramp = this.config.rampSeconds ?? 0;
    if (this.active && ramp > 0) this.rise = Math.min(this.rise, this.seconds / ramp);
    this.seconds = this.config.seconds;
  }

  /** Counts down; returns true in the step the turbo ran out. */
  update(dt: number): boolean {
    if (this.seconds <= 0) return false;
    const ramp = this.config.rampSeconds ?? 0;
    this.rise = ramp > 0 ? Math.min(1, this.rise + dt / ramp) : 1;
    this.seconds = Math.max(0, this.seconds - dt);
    if (this.seconds > 0) return false;
    this.rise = 0;
    return true;
  }

  reset(): void {
    this.seconds = 0;
    this.rise = 0;
  }
}
